import React from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { fetchLanguages, sortLanguages } from 'wwtc-sdk';
import Main from './components/Main.js';
import ChatWrapper from './components/Chat.js';
import Invitation from './components/Invitation.js';
import Login from './components/Login.js';
import {
    getActiveWaitingRoom,
    getAllowJoinWithoutModerator,
    getCanKickParticipants,
} from './utils/manifest.js';

function App() {
    const [state, setState] = React.useState({
        languages: [],
        status: 'loading',
    });

    React.useEffect(() => {
        fetchCompanyInfo().then((response) => {
            // Check if response and response.data exist
            if (!response || !response.data) {
                console.error('API response is invalid:', response);
                // Set fallback values for local development
                window.room = 'local-dev-room';
                window.logoHeader = '/logo-header.png';
                window.logoLogin = '/logo-login.png';
                window.cognitoUserPoolId = 'dev-pool-id';
                window.cognitoClientId = 'dev-client-id';
                window.apiToken = 'dev-api-token';
                window.manifestId = 'dev-manifest-id';
                window.dictionaries = [];
                window.canKickParticipants = getCanKickParticipants();
                window.activeLanguageDetection = false;
                window.activeWaitingRoom = getActiveWaitingRoom();
                window.allowJoinWithoutModerator = getAllowJoinWithoutModerator();

                // Continue with default languages
                fetchLanguages().then((languages) => {
                    setState({
                        status: 'resolved',
                        languages,
                    });
                });
                return;
            }

            window.room = response.data.pubnubRoom || 'fallback-room';
            window.logoHeader = response.data.logoHeader || '/logo-header.png';
            window.logoLogin = response.data.logoLogin || '/logo-login.png';
            window.cognitoUserPoolId = response.data.cognitoUserPoolId || 'fallback-pool-id';
            window.cognitoClientId = response.data.cognitoClientId || 'fallback-client-id';
            window.apiToken = response.data.apiToken || 'fallback-api-token';
            window.manifestId = response.data.id || 'fallback-manifest-id';
            window.dictionaries = response.data.dictionaries || [];
            window.canKickParticipants = getCanKickParticipants(response.data);
            window.activeLanguageDetection = response.data.activeLanguageDetection || false;
            window.activeWaitingRoom = getActiveWaitingRoom(response.data);
            window.allowJoinWithoutModerator = getAllowJoinWithoutModerator(response.data);
            window.enableAudit = response.data.enableAudit || false;
            window.bilingualTextColor = response.data.bilingualTextColor;
            // set palette color
            if (!!response.data.theme) {
                setPaletteColor(response.data.theme);
            }

            // custom language list
            // from dashboard general settings
            if (response.data.languages && response.data.languages.length > 0) {
                setState({
                    status: 'resolved',
                    languages: sortLanguages(response.data.languages),
                });
            } else {
                // language came already sorted
                fetchLanguages().then((languages) => {
                    setState({
                        status: 'resolved',
                        languages,
                    });
                });
            }
        });
    }, []);

    /**
     * Set color palette to css variables :root (src/index.css)
     * @param {object} theme Color palette properties
     */
    const setPaletteColor = (theme) => {
        document.documentElement.style.setProperty(
            '--main-bg-button-primary',
            theme.bgColorLoginButton,
        );
        document.documentElement.style.setProperty(
            '--main-color-button-primary',
            theme.mainButtonTextColor,
        );
        document.documentElement.style.setProperty('--header-color-text', theme.headerTextColor);
        document.documentElement.style.setProperty('--gradient-primary', theme.gradientPrimary);
        document.documentElement.style.setProperty('--gradient-secondary', theme.gradientSecondary);
        document.documentElement.style.setProperty(
            '--component-header-option',
            theme.textHeaderComponents,
        );

        if (!!theme.primaryColor) {
            document.documentElement.style.setProperty('--main-primary', theme.primaryColor);
        }
    };

    const fetchCompanyInfo = async () => {
        try {
            const response = await fetch(
                'https://api.worldwidetechconnections.com/services/manifest/v1/now-translate',
            );

            if (!response.ok) {
                console.warn(`API response not OK: ${response.status} ${response.statusText}`);
                return null;
            }

            const data = await response.json();
            return data;
        } catch (e) {
            console.error('Error fetching company info:', e);
            return null;
        }
    };

    if (state.status === 'loading') {
        return (
            <div className="loader-wrapper">
                <div className="text-center">
                    <h1 className="loader-title">Loading...</h1>
                </div>
            </div>
        );
    }
    return (
        // If project will be store at subfolder
        // you should define the basename with the name of the folder
        // If project will be store at root
        // basename = undefined
        <Router basename={window.location.pathname.replace(/(\/[^/]+)$/, '')}>
            <Switch>
                <Route path="/chat">
                    <ChatWrapper languages={state.languages} />
                </Route>
                <Route path="/invitation">
                    <Invitation languages={state.languages} />
                </Route>
                <Route path="/app">
                    <Main />
                </Route>
                <Route path="/">
                    <Login languages={state.languages} />
                </Route>
            </Switch>
        </Router>
    );
}
export default App;
